import * as React from 'react'

import { useSiteConfig } from '@thg-commerce/enterprise-core'
import SvgIcon from '@thg-commerce/gravity-icons/src/components/SvgIcon'
import { useTheme } from '@thg-commerce/gravity-patterns/theme'
import { spacing } from '@thg-commerce/gravity-theme'

import { StoreOpeningTime } from '../StoreSearch/types'

import { Facilities, StoreFacility } from './Facilities/Facilities'
import { Status, StoreStatus } from './StoreStatus'
import {
  CardFooterContainer,
  DetailsCardContainer,
  MapPinWrapper,
  NameWrapper,
  SelectedIconWrapper,
  StoreIcon,
  StyledButton,
} from './styles'

export interface StoreCard {
  id: string
  displayName: string
  relativeDistance?: number
  openingTimes: StoreOpeningTime[]
  status?: Status
  storeFacility: StoreFacility[]
  address: string
  isOpen?: boolean
}

export interface I18nText {
  storeOpenText?: string
  storeClosedText?: string
  storeTempClosed?: string
  storePermClosed?: string
  storeDetailsButton?: string
  getDirectionsButton?: string
  milesText?: string
}

interface StoreDetailsCardProps {
  selected?: boolean
  searchCard?: boolean
  searchNo?: number
  compactCard?: boolean
  hideCloseIcon?: boolean
  store?: StoreCard
  i18n?: I18nText
  storeDetailsHref?: string
  storeDetailsOnClick?: () => void
  getDirectionsHref?: string
  fullWidthCard?: boolean
  entireCardOnClick?: React.Dispatch<React.SetStateAction<number>>
}

export const StoreDetailsCard = (props: StoreDetailsCardProps) => {
  const theme = useTheme()
  const { hideStoreLocatorOpeningTimes } = useSiteConfig()
  const selectedIcon =
    props.searchCard && props.compactCard
      ? theme.icons.closeCross
      : theme.icons.selectedCard
  const selectedCardRef = React.createRef<HTMLDivElement>()

  const currentDayIndex = new Date().getDay()
  React.useEffect(() => {
    if (props.selected) {
      selectedCardRef?.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }, [props.selected])

  return (
    <DetailsCardContainer
      selected={props.selected}
      compactCard={props.compactCard}
      fullWidthCard={props.fullWidthCard}
      ref={selectedCardRef}
      clickable={!!props.entireCardOnClick}
      onClick={() =>
        props.searchNo &&
        props.entireCardOnClick &&
        props.entireCardOnClick(props.searchNo)
      }
    >
      {props.selected && !props.hideCloseIcon && (
        <SelectedIconWrapper
          searchCompactCard={props.searchCard && props.compactCard}
        >
          <SvgIcon
            xmlns="http://www.w3.org/2000/svg"
            height={selectedIcon?.height}
            width={selectedIcon?.width}
            viewBox={selectedIcon?.viewBox}
          >
            <rect
              width={selectedIcon?.width}
              height={selectedIcon?.height}
              fill="#FFFFFF"
            />
            <path d={selectedIcon?.svgPath} />
          </SvgIcon>
        </SelectedIconWrapper>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: `${spacing(1)}`,
        }}
        data-testid="title-container"
      >
        <NameWrapper data-testid="name-wrapper">
          {props.searchCard ? (
            theme.icons.mapPin?.svgPath && (
              <MapPinWrapper selected={props.selected}>
                <SvgIcon
                  xmlns="http://www.w3.org/2000/svg"
                  height={theme.icons.mapPin?.height}
                  width={theme.icons.mapPin?.width}
                  viewBox={theme.icons.mapPin?.viewBox}
                >
                  <path d={theme.icons.mapPin?.svgPath} />
                </SvgIcon>
                <span>{props.searchNo}</span>
              </MapPinWrapper>
            )
          ) : (
            <StoreIcon />
          )}
          <a
            href={props.storeDetailsHref}
            onClick={(e) => {
              e.stopPropagation()
            }}
            role="button"
          >
            {props.store?.displayName}
          </a>
        </NameWrapper>
        <p>
          {props.store?.relativeDistance?.toFixed(1)} {props.i18n?.milesText}
        </p>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: `${spacing(1)}`,
        }}
        data-testid="info-wrapper"
      >
        <p>{props.store?.address}</p>

        {!hideStoreLocatorOpeningTimes && (
          <StoreStatus
            openingTimes={props.store?.openingTimes[currentDayIndex]}
            status={props.store?.status}
            i18n={props.i18n}
            isOpen={props.store?.status === Status.OPEN}
          />
        )}
        {!props.compactCard && (
          <Facilities storeFacility={props.store?.storeFacility} />
        )}
      </div>
      <CardFooterContainer data-testid="card-footer">
        <StyledButton
          data-testid="card-store-details-button"
          emphasis="high"
          type="button"
          renderedAs="a"
          href={props.storeDetailsHref}
          onClick={(e) => {
            props.storeDetailsOnClick && props.storeDetailsOnClick()
            e.stopPropagation()
          }}
          compactCard={props.compactCard}
        >
          {props.i18n?.storeDetailsButton}
        </StyledButton>
        <StyledButton
          data-testid="card-get-directions-button"
          emphasis="medium"
          type="button"
          renderedAs="a"
          href={props.getDirectionsHref}
          target="_blank"
          onClick={(e) => {
            e.stopPropagation()
          }}
          compactCard={props.compactCard}
        >
          {props.i18n?.getDirectionsButton}
        </StyledButton>
      </CardFooterContainer>
    </DetailsCardContainer>
  )
}
